/**
 * MigratePage.tsx — Sentry → ɳSentry migration guide
 *
 * Purpose:    Interactive migration guide with cost calculator, DSN converter, SDK diff table,
 *             env var rename table, SDK code examples, and step-by-step migration steps
 * Inputs:     SDK_DIFF, ENV_VAR_MAP, convertDsn from lib/migration-data
 * Outputs:    Full-page migration guide
 * Constraints: Client-side; all interactive state via useState
 * SOT:        T-P3-E3-W2-S5-T01
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { SDK_DIFF, ENV_VAR_MAP, convertDsn } from '../lib/migration-data'

export function MigratePage() {
  const [monthlyEvents, setMonthlyEvents] = useState(100_000)
  const [sentryDsn, setSentryDsn] = useState('')
  const [apiHost, setApiHost] = useState('api.local.nself.org')
  const convertedDsn = convertDsn(sentryDsn, apiHost)
  const sentryMonthly = Math.max(Math.round((monthlyEvents / 1_000_000) * 29), 10)
  const nsentryMonthly = 0.99
  const savings = Math.max(sentryMonthly - nsentryMonthly, 0)

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink">
      <header className="border-b border-brand-subtle bg-brand-surface/50 py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-sky-200 dark:border-sky-800/50 bg-sky-50 dark:bg-sky-950/50 px-3 py-1 text-sm text-sky-700 dark:text-sky-300">
            Migration guide
          </div>
          <h1 className="mb-4 text-4xl font-bold">Migrate from Sentry to ɳSentry</h1>
          <p className="text-lg text-brand-muted">
            Same SDK protocol, lower cost, no data leaving your server.
            Most migrations complete in under 10 minutes — you only change the DSN URL.
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-16 px-6 py-16">

        {/* Why migrate */}
        <section aria-labelledby="why-heading">
          <h2 id="why-heading" className="mb-6 text-2xl font-bold text-sky-500">
            Why migrate?
          </h2>
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              {
                title: 'Privacy-first',
                body: 'All error data, uptime results, and RUM sessions stay on your server. No third-party SaaS touches your data.',
              },
              {
                title: 'Flat $0.99/mo',
                body: 'No per-event pricing. No retention tiers. No overage fees. Sentry SaaS starts at $26+/mo before you hit any real usage.',
              },
              {
                title: 'Same SDK',
                body: 'ɳSentry implements the Sentry envelope protocol. Swap the DSN URL — your existing SDK initialisation works without changes.',
              },
            ].map((item) => (
              <div key={item.title} className="rounded-xl border border-brand-subtle bg-brand-surface/40 p-6">
                <h3 className="mb-2 font-semibold text-brand-ink">{item.title}</h3>
                <p className="text-sm text-brand-muted">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SDK diff table */}
        <section aria-labelledby="sdk-diff-heading">
          <h2 id="sdk-diff-heading" className="mb-4 text-2xl font-bold text-sky-500">
            SDK compatibility
          </h2>
          <p className="mb-6 text-sm text-brand-muted">
            ɳSentry implements the full Sentry envelope protocol. Most SDK packages need only a DSN swap.
          </p>
          <div className="overflow-x-auto rounded-xl border border-brand-subtle">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-brand-subtle bg-brand-surface/60">
                  <th className="px-4 py-3 text-left font-semibold text-brand-secondary">SDK</th>
                  <th className="px-4 py-3 text-left font-semibold text-brand-secondary">Package change</th>
                  <th className="px-4 py-3 text-left font-semibold text-brand-secondary">Notes</th>
                </tr>
              </thead>
              <tbody>
                {SDK_DIFF.map((row, i) => (
                  <tr
                    key={row.lang}
                    className={`border-b border-brand-subtle/60 ${i % 2 === 0 ? 'bg-brand-surface/20' : ''}`}
                  >
                    <td className="px-4 py-3 font-medium text-sky-700 dark:text-sky-300">{row.lang}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        row.packageChange === 'none'
                          ? 'border border-emerald-200 dark:border-emerald-700/50 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400'
                          : 'border border-yellow-200 dark:border-yellow-700/50 bg-yellow-50 dark:bg-yellow-950/40 text-yellow-700 dark:text-yellow-400'
                      }`}>
                        {row.packageChange === 'none' ? 'DSN only' : 'Optional'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-brand-muted">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Step-by-step migration */}
        <section aria-labelledby="steps-heading">
          <h2 id="steps-heading" className="mb-4 text-2xl font-bold text-sky-500">
            Step-by-step migration
          </h2>
          <ol className="space-y-4">
            {[
              {
                num: 1,
                title: 'Install nSelf + ɳSentry bundle',
                code: 'nself plugin install nself-uptime-monitor nself-status-page nself-incident-mgmt nself-alert-router nself-slo-tracker nself-synthetic-monitor nself-rum nself-errors nself-cron-monitor nself-oncall nself-crash nself-anomaly nself-audit',
              },
              {
                num: 2,
                title: 'Get your ɳSentry DSN',
                code: 'curl http://api.local.nself.org/sentry/dsn',
              },
              {
                num: 3,
                title: 'Update your app DSN (same SDK, new URL)',
                code: null,
              },
              {
                num: 4,
                title: 'Export old data from Sentry SaaS (optional)',
                code: null,
              },
            ].map(({ num, title, code }) => (
              <li key={num} className="flex gap-4">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-sky-500 font-bold text-gray-950">
                  {num}
                </span>
                <div className="flex-1">
                  <h3 className="font-semibold">{title}</h3>
                  {code && (
                    <code className="mt-2 block overflow-x-auto rounded bg-brand-surface p-3 text-sm text-sky-700 dark:text-sky-400 whitespace-pre-wrap break-all">
                      {code}
                    </code>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* DSN converter */}
        <section aria-labelledby="dsn-converter-heading">
          <h2 id="dsn-converter-heading" className="mb-2 text-2xl font-bold text-sky-500">
            DSN converter
          </h2>
          <p className="mb-4 text-sm text-brand-muted">
            Paste your Sentry SaaS DSN to see the equivalent ɳSentry DSN format.
            The actual DSN is generated by your ɳSentry instance — this tool shows the expected structure.
          </p>

          <div className="space-y-4 rounded-xl border border-brand-subtle bg-brand-surface/40 p-6">
            <div>
              <label htmlFor="sentry-dsn" className="mb-1 block text-sm font-medium text-brand-secondary">
                Sentry SaaS DSN
              </label>
              <input
                id="sentry-dsn"
                type="url"
                value={sentryDsn}
                onChange={(e) => setSentryDsn(e.target.value)}
                placeholder="https://abc123@o123456.ingest.sentry.io/789"
                className="w-full rounded-lg border border-brand-card bg-brand-surface px-4 py-2.5 font-mono text-sm text-brand-ink placeholder-gray-600 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                aria-describedby="dsn-hint"
              />
              <p id="dsn-hint" className="mt-1 text-xs text-brand-muted">
                Found in Sentry → Settings → Client Keys (DSN).
              </p>
            </div>
            <div>
              <label htmlFor="api-host" className="mb-1 block text-sm font-medium text-brand-secondary">
                ɳSentry API host
              </label>
              <input
                id="api-host"
                type="text"
                value={apiHost}
                onChange={(e) => setApiHost(e.target.value)}
                placeholder="api.local.nself.org"
                className="w-full rounded-lg border border-brand-card bg-brand-surface px-4 py-2.5 font-mono text-sm text-brand-ink placeholder-gray-600 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              />
            </div>
            {convertedDsn && (
              <div>
                <p className="mb-1 text-sm font-medium text-brand-secondary">ɳSentry DSN</p>
                <code className="block w-full overflow-x-auto rounded-lg bg-brand-bg p-3 font-mono text-sm text-sky-700 dark:text-sky-300">
                  {convertedDsn}
                </code>
              </div>
            )}
            {sentryDsn && !convertedDsn && (
              <p className="text-sm text-red-700 dark:text-red-400">Could not parse — check that the DSN is a valid Sentry URL.</p>
            )}
          </div>
        </section>

        {/* Env var rename table */}
        <section aria-labelledby="env-vars-heading">
          <h2 id="env-vars-heading" className="mb-2 text-2xl font-bold text-sky-500">
            Environment variable changes
          </h2>
          <p className="mb-6 text-sm text-brand-muted">
            Most Sentry env vars are unchanged. Only the DSN value and auth token var rename.
          </p>
          <div className="overflow-x-auto rounded-xl border border-brand-subtle">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-brand-subtle bg-brand-surface/60">
                  <th className="px-4 py-3 text-left font-semibold text-brand-secondary">Sentry var</th>
                  <th className="px-4 py-3 text-left font-semibold text-brand-secondary">ɳSentry var</th>
                  <th className="px-4 py-3 text-left font-semibold text-brand-secondary">Purpose</th>
                </tr>
              </thead>
              <tbody>
                {ENV_VAR_MAP.map((row, i) => (
                  <tr
                    key={row.sentryVar}
                    className={`border-b border-brand-subtle/60 ${i % 2 === 0 ? 'bg-brand-surface/20' : ''}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-sky-700 dark:text-sky-300">{row.sentryVar}</td>
                    <td className="px-4 py-3 font-mono text-xs text-sky-700 dark:text-sky-400">{row.nsentryVar || '—'}</td>
                    <td className="px-4 py-3 text-xs text-brand-muted">{row.purpose}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* SDK code examples */}
        <section aria-labelledby="sdk-examples-heading">
          <h2 id="sdk-examples-heading" className="mb-4 text-2xl font-bold text-sky-500">
            SDK code examples
          </h2>
          <div className="space-y-4">
            {[
              {
                lang: 'JavaScript / TypeScript',
                code: `Sentry.init({
  dsn: "https://<key>@api.local.nself.org/sentry/<project-id>",
  tracesSampleRate: 1.0,
})`,
              },
              {
                lang: 'Python',
                code: `sentry_sdk.init(
    dsn="https://<key>@api.local.nself.org/sentry/<project-id>",
)`,
              },
              {
                lang: 'Go',
                code: `sentry.Init(sentry.ClientOptions{
    Dsn: "https://<key>@api.local.nself.org/sentry/<project-id>",
})`,
              },
              {
                lang: 'Ruby',
                code: `Sentry.init do |config|
  config.dsn = "https://<key>@api.local.nself.org/sentry/<project-id>"
end`,
              },
            ].map(({ lang, code }) => (
              <div key={lang}>
                <h3 className="mb-2 font-semibold text-sky-700 dark:text-sky-400">{lang}</h3>
                <pre className="overflow-x-auto rounded bg-brand-surface p-4 text-sm text-brand-secondary">
                  <code>{code}</code>
                </pre>
              </div>
            ))}
          </div>
        </section>

        {/* Export from Sentry */}
        <section aria-labelledby="export-heading">
          <h2 id="export-heading" className="mb-4 text-2xl font-bold text-sky-500">
            Export historical data from Sentry SaaS
          </h2>
          <ol className="space-y-2 text-sm text-brand-secondary">
            <li>1. Log into sentry.io → Settings → Data Export</li>
            <li>2. Download CSV for the projects you want to migrate</li>
            <li>3. Import via CLI: <code className="rounded bg-brand-surface px-2 py-1">nself sentry import --file data.csv</code></li>
          </ol>
        </section>

        {/* Cost savings calculator */}
        <section aria-labelledby="calculator-heading">
          <h2 id="calculator-heading" className="mb-4 text-2xl font-bold text-sky-500">
            Cost savings calculator
          </h2>
          <div className="rounded-lg border border-brand-subtle bg-brand-surface p-6">
            <label htmlFor="monthly-events" className="mb-2 block text-sm font-medium">
              Monthly events
            </label>
            <input
              id="monthly-events"
              type="range"
              min="10000"
              max="10000000"
              step="10000"
              value={monthlyEvents}
              onChange={(e) => setMonthlyEvents(Number(e.target.value))}
              className="mb-2 w-full accent-sky-500"
              aria-valuenow={monthlyEvents}
              aria-valuemin={10000}
              aria-valuemax={10000000}
            />
            <p className="mb-4 text-sm text-brand-muted">{monthlyEvents.toLocaleString()} events/month</p>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-brand-muted">Sentry SaaS (estimated)</p>
                <p className="text-2xl font-bold">${sentryMonthly}/mo</p>
              </div>
              <div>
                <p className="text-sm text-brand-muted">ɳSentry</p>
                <p className="text-2xl font-bold text-sky-700 dark:text-sky-400">$0.99/mo</p>
              </div>
            </div>
            <div className="mt-4 rounded bg-brand-surface-2 p-4">
              <p className="text-sm text-brand-muted">You save</p>
              <p className="text-3xl font-bold text-green-700 dark:text-green-400">${savings.toFixed(2)}/mo</p>
              <p className="text-xs text-brand-muted">${(savings * 12).toFixed(0)}/year</p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="mb-4 text-2xl font-bold text-sky-500">FAQ</h2>
          <div className="space-y-3">
            {[
              {
                q: 'Do I need to rewrite my error handling?',
                a: 'No. ɳSentry implements the Sentry envelope protocol. Just swap the DSN URL — your existing SDK code works without changes.',
              },
              {
                q: 'Can I run ɳSentry on my own VPS?',
                a: 'Yes. Install nSelf on any Linux VPS (Hetzner, DigitalOcean, Linode, AWS) and activate the ɳSentry bundle.',
              },
              {
                q: 'What about historical error data?',
                a: 'Export via Sentry SaaS → Data Export and import using `nself sentry import --file data.csv`.',
              },
              {
                q: 'Is ɳSentry production-ready?',
                a: 'ɳSentry ships with nSelf v1.1.0 (planned). The nSelf team runs the full ɳSentry bundle in production on nself.org.',
              },
              {
                q: 'What is nself-audit and is it really free?',
                a: 'nself-audit is the tamper-evident audit log plugin (port 3843). Per the Security-Always-Free doctrine, it is free in all tiers including the free nSelf tier — no license key required.',
              },
            ].map(({ q, a }) => (
              <details key={q} className="rounded border border-brand-subtle bg-brand-surface p-4">
                <summary className="cursor-pointer font-semibold text-sky-700 dark:text-sky-400">{q}</summary>
                <p className="mt-2 text-sm text-brand-muted">{a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      <footer className="mt-16 border-t border-brand-subtle bg-brand-surface py-12">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="mb-4 text-2xl font-bold">Ready to migrate?</h2>
          <p className="mb-6 text-brand-muted">Install nSelf and the ɳSentry bundle in under 5 minutes.</p>
          <Link
            to="/pricing"
            className="inline-block rounded-lg bg-sky-500 px-6 py-3 font-semibold text-white hover:bg-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-gray-900"
          >
            Get started — $0.99/mo
          </Link>
        </div>
      </footer>
    </div>
  )
}
